import { currentCurrency, money2 } from "@/lib/format";

export const EMPTY_FORM = {
  main_menu_id: "",
  sub_menu_id: "",
  code: "",
  name: "",
  name_ar: "",
  alias_name: "",
  description: "",
  unit: "Plate",
  product_type: "Finished",
  code_type: "Standard",
  cost_ex_vat: "",
  cost_inc_vat: "",
  retail_ex: "",
  price: "",
  dealer_price: "",
  discount_percent: "0",
  vat_percent: "5",
  tax_group: "Standard",
  tax_inclusive: true,
  small_price: "",
  medium_price: "",
  large_price: "",
  reorder_level: "0",
  is_kot: true,
  kitchen_station_id: "",
  show_in_pos: true,
  show_on_online_catalog: false,
  online_name: "",
  online_description: "",
  allow_discount: true,
  open_price: false,
  image_url: "",
  status: "Active",
};

function numStr(value) {
  if (value === null || value === undefined || value === "") return "";
  return String(value);
}

export function num(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

export function roundMoney(value, digits) {
  const dp = digits ?? currentCurrency().decimals ?? 2;
  return money2(value, undefined, dp);
}

/** Exclusive → inclusive */
export function withVat(ex, vatPercent) {
  return num(ex) * (1 + num(vatPercent) / 100);
}

/** Inclusive → exclusive */
export function withoutVat(inc, vatPercent) {
  const vat = num(vatPercent);
  return vat > 0 ? num(inc) / (1 + vat / 100) : num(inc);
}

export function itemToForm(item, digits) {
  if (!item) return { ...EMPTY_FORM };
  const dp = digits ?? currentCurrency().decimals ?? 2;

  const vat = num(item.vat_percent ?? item.vat ?? 5);
  const taxInclusive = item.tax_inclusive ?? true;
  const costEx = item.cost != null && item.cost !== "" ? num(item.cost) : null;

  const hasSizes = [item.smallPrice, item.mediumPrice, item.largePrice]
    .some((v) => v !== null && v !== undefined);

  let retailInc = "";
  let retailEx = "";
  if (!hasSizes && item.price != null && item.price !== "") {
    const stored = num(item.price);
    if (taxInclusive) {
      retailInc = roundMoney(stored, dp);
      retailEx = roundMoney(withoutVat(stored, vat), dp);
    } else {
      retailEx = roundMoney(stored, dp);
      retailInc = roundMoney(withVat(stored, vat), dp);
    }
  }

  return {
    main_menu_id: item.main_menu_id ? String(item.main_menu_id) : "",
    sub_menu_id: item.sub_menu_id ? String(item.sub_menu_id) : "",
    code: item.code || "",
    name: item.name || "",
    name_ar: item.name_ar || "",
    alias_name: item.alias_name || "",
    description: item.description || "",
    unit: item.unit || "Plate",
    product_type: item.product_type || "Finished",
    code_type: item.code_type || "Standard",
    cost_ex_vat: costEx != null ? roundMoney(costEx, dp) : "",
    cost_inc_vat: costEx != null ? roundMoney(withVat(costEx, vat), dp) : "",
    retail_ex: retailEx,
    price: retailInc,
    dealer_price: item.dealer_price != null && item.dealer_price !== "" ? roundMoney(item.dealer_price, dp) : "",
    discount_percent: "0",
    vat_percent: numStr(item.vat_percent ?? item.vat ?? 5),
    tax_group: item.tax_group || "Standard",
    tax_inclusive: taxInclusive,
    small_price: item.smallPrice != null && item.smallPrice !== "" ? roundMoney(item.smallPrice, dp) : "",
    medium_price: item.mediumPrice != null && item.mediumPrice !== "" ? roundMoney(item.mediumPrice, dp) : "",
    large_price: item.largePrice != null && item.largePrice !== "" ? roundMoney(item.largePrice, dp) : "",
    reorder_level: numStr(item.reorder ?? 0),
    is_kot: item.kot ?? true,
    kitchen_station_id: item.kitchen_station_id ? String(item.kitchen_station_id) : "",
    show_in_pos: item.show_in_pos ?? true,
    show_on_online_catalog: item.show_on_online_catalog ?? false,
    online_name: item.online_name || "",
    online_description: item.online_description || "",
    allow_discount: item.allow_discount ?? true,
    open_price: item.open_price ?? false,
    image_url: item.img || "",
    status: item.status || "Active",
  };
}

export function previewNextMenuCode(items) {
  let max = 0;
  const used = new Set();
  for (const item of items || []) {
    const code = String(item.code || "").trim();
    if (code) used.add(code);
    const match = code.match(/^(?:MI-)?(\d+)$/i);
    if (match) max = Math.max(max, Number(match[1]));
  }
  for (let n = max + 1; n < max + 10000; n++) {
    const candidate = `MI-${String(n).padStart(4, "0")}`;
    if (!used.has(candidate)) return candidate;
  }
  return "MI-AUTO";
}

export function formHasSizePrices(form) {
  return [form.small_price, form.medium_price, form.large_price]
    .some((v) => v !== "" && v !== null && v !== undefined && !Number.isNaN(Number(v)));
}

export function formToPayload(form) {
  // Prefer size variants when any are set (mutually exclusive with single retail).
  const hasSizes = formHasSizePrices(form);
  const hasRetail = !hasSizes && form.price !== "" && !Number.isNaN(Number(form.price));
  const small = hasSizes && form.small_price !== "" ? Number(form.small_price) : null;
  const medium = hasSizes && form.medium_price !== "" ? Number(form.medium_price) : null;
  const large = hasSizes && form.large_price !== "" ? Number(form.large_price) : null;
  const variantDefault = [medium, large, small].find((v) => v !== null && !Number.isNaN(v));

  // Persist cost exclusive; persist selling as inclusive when tax_inclusive, else exclusive.
  const retailInc = hasRetail ? Number(form.price) : null;
  const retailEx = form.retail_ex !== "" ? Number(form.retail_ex) : null;
  const storedPrice = hasRetail
    ? (form.tax_inclusive ? retailInc : (retailEx ?? withoutVat(retailInc, form.vat_percent)))
    : (variantDefault ?? 0);

  const payload = {
    main_menu_id: form.main_menu_id ? Number(form.main_menu_id) : null,
    sub_menu_id: form.sub_menu_id ? Number(form.sub_menu_id) : null,
    name: form.name.trim(),
    name_ar: form.name_ar?.trim() || null,
    alias_name: null,
    description: form.description?.trim() || null,
    unit: form.unit,
    product_type: form.product_type,
    code_type: form.code_type,
    cost: form.cost_ex_vat === "" ? 0 : Number(form.cost_ex_vat) || 0,
    price: storedPrice,
    dealer_price: null,
    discount_percent: 0,
    vat_percent: Number(form.vat_percent) || 0,
    tax_group: form.tax_group,
    tax_inclusive: form.tax_inclusive,
    small_price: small,
    medium_price: medium,
    large_price: large,
    reorder_level: Number(form.reorder_level) || 0,
    is_kot: Boolean(form.kitchen_station_id),
    kitchen_station_id: form.kitchen_station_id ? Number(form.kitchen_station_id) : null,
    show_in_pos: form.show_in_pos,
    show_on_online_catalog: form.show_on_online_catalog,
    online_name: form.show_on_online_catalog ? (form.online_name?.trim() || null) : null,
    online_description: form.show_on_online_catalog ? (form.online_description?.trim() || null) : null,
    allow_discount: form.allow_discount,
    open_price: form.open_price,
    image_url: form.image_url?.trim() || null,
    status: form.status,
  };
  return payload;
}

export function apiError(err, fallback) {
  const errors = err.response?.data?.errors;
  if (errors) return Object.values(errors).flat()[0];
  return err.response?.data?.message || fallback;
}

export function hasRetailPrice(form) {
  return form.price !== "" && form.price !== null && form.price !== undefined;
}

export function validateForm(form) {
  if (!form.sub_menu_id) return "Subcategory is required.";
  if (!form.name?.trim()) return "Menu item name is required.";
  if (formHasSizePrices(form)) {
    const sizes = [form.small_price, form.medium_price, form.large_price]
      .filter((v) => v !== "" && v !== null && v !== undefined);
    if (sizes.some((v) => Number.isNaN(Number(v)) || Number(v) < 0)) {
      return "Size prices must be valid non-negative numbers.";
    }
    return null;
  }
  if (hasRetailPrice(form)) {
    if (Number.isNaN(Number(form.price))) return "Retail inclusive must be a number.";
    if (Number(form.price) < 0) return "Retail price cannot be negative.";
    return null;
  }
  return "Enter a retail price, or set Small / Medium / Large prices.";
}

export function listPriceLabel(item, aed) {
  const sizes = [item.smallPrice, item.mediumPrice, item.largePrice]
    .filter((v) => v !== null && v !== undefined)
    .map(Number);
  if (sizes.length) {
    const min = Math.min(...sizes);
    const max = Math.max(...sizes);
    return min === max ? aed(min) : `${aed(min)}–${aed(max)}`;
  }
  return aed(item.price || 0);
}

export function priceSortValue(item) {
  const sizes = [item.smallPrice, item.mediumPrice, item.largePrice]
    .filter((v) => v !== null && v !== undefined)
    .map(Number);
  if (sizes.length) return Math.min(...sizes);
  return Number(item.price || 0);
}
